import React, { useState, useEffect ,useRef} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AxiosInstance from '../AxiosInstance';
import moment from 'moment-timezone';
import 'bootstrap/dist/css/bootstrap.min.css';
import ELayout from './ELayout';
import { ArrowLeftCircle } from "react-bootstrap-icons";
import { Button } from "react-bootstrap";
import ToastNotification from '../ToastNotification';

const TimesheetTable = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const toastRef = useRef(null); // Toast notification reference
  const { week, mode,fromPageState } = location.state || {};
  const { projects: assignedProjects } = location.state || {};
  const [timesheetData, setTimesheetData] = useState([]);

  const [projects, setProjects] = useState([]);
  const [currentDate, setCurrentDate] = useState('');
  const [weekDates, setWeekDates] = useState([]);
  const [holidays, setHolidays] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const employeeId = localStorage.getItem('employeeId');

  const [leaveDates, setLeaveDates] = useState([]);
  const [holidayDates, setHolidayDates] = useState([]);
  const [leaveWorkHours, setLeaveWorkHours] = useState(""); 

  useEffect(() => {
    
    if (Array.isArray(assignedProjects)) {
      setProjects(assignedProjects);
    } else {
      setProjects([]);
    }
  
    if (week?.week_start) {
      setCurrentDate(week.week_start);
      calculateWeekDates(week.week_start,week.week_end); // Calculate week dates based on week_start
    }
  }, [assignedProjects, week]);

  useEffect(() => {
    if (weekDates.length > 0 && employeeId) {
      fetchAssignedProjects();
      fetchPublicHolidays(weekDates);
    }
  }, [weekDates, employeeId]);

  const calculateWeekDates = (weekStartDate, weekEndDate) => {
    if (!weekStartDate || !weekEndDate) return;

    const start = moment(weekStartDate).tz('Asia/Kolkata');
    const end = moment(weekEndDate).tz('Asia/Kolkata');

    const dates = [];
    let current = start.clone();

    while (current.isSameOrBefore(end, 'day')) {
        dates.push(current.format('YYYY-MM-DD'));
        current.add(1, 'day');
    }

    setWeekDates(dates);
};


  const handleBack = () => {
    navigate("/weeksTimesheet", { state: fromPageState });
  };

  const fetchAssignedProjects = async () => {
    try {
      const response = await AxiosInstance.get('/employee/api/projects', {
        params: { employee_id: employeeId },
      });
  
      if (response.data) {
        const uniqueProjects = [
          ...new Set(response.data.map(project => project.project_id))
        ].map(id => response.data.find(project => project.project_id === id));
  
        setProjects(uniqueProjects);
      }
    } catch (error) {
       toastRef.current?.showToast('Error fetching employee projects:',"danger");
    }
  };
    
  useEffect(() => {
    async function fetchTimesheet() {
        try {
            if (employeeId && week?.week_start && week?.week_end) {
                const response = await AxiosInstance.get(`/employee/work_hours/week`, {
                    params: {
                        employee_id: employeeId,
                        week_start_date: week.week_start,
                        week_end_date: week.week_end,
                    },
                });

                if (response.data) {
                    setTimesheetData(response.data.workHours);
                    setLeaveDates(response.data.leaveDates);
                    setHolidayDates(response.data.holidayDates);
                    setLeaveWorkHours(response.data.leaveWorkHours); 
                }
            }
        } catch (error) {
             toastRef.current?.showToast("Error fetching timesheet data:", "danger");
        }
    }

    fetchTimesheet();
}, [employeeId, week?.week_start, week?.week_end]);

  
  useEffect(() => {
    if (timesheetData.length > 0) {
      setProjects((prevProjects) =>
        prevProjects.map((project) => {
          const projectTimesheetEntries = timesheetData.filter(
            (entry) => entry.project_id === project.project_id
          );
  
          const updatedProject = { ...project };
  
          projectTimesheetEntries.forEach((entry) => {
            const day = moment(entry.work_date).format('dddd').toLowerCase();
            updatedProject[day] = entry.work_hours; 
          });
  
          return updatedProject;
        })
      );
    }
  }, [timesheetData]);
   

  const fetchPublicHolidays = async (dates) => {
    try {
      const response = await AxiosInstance.get('employee/api/public-holidays', {
        params: { dates },
      });
      setHolidays(response.data || {});
    } catch (error) {
       toastRef.current?.showToast('Error fetching public holidays:',"danger");
    }
  };

  const handleInputChange = (projectId, day, value) => {
    if (value === '') {
      setProjects((prevProjects) =>
        prevProjects.map((project) =>
          project.project_id === projectId ? { ...project, [day]: '' } : project
        )
      );
      return;
    }
  
    const hours = Number(value);
  
    if (isNaN(hours) || hours < 0 || hours > 8) {
      toastRef.current.showToast('Please enter a valid number of hours (0-8)',"warning");
      return;
    }
  
    setProjects((prevProjects) =>
      prevProjects.map((project) =>
        project.project_id === projectId
          ? {
              ...project,
              [day]: hours,
              total_weekly_hours: Object.entries({
                ...project,
                [day]: hours,
              }).reduce(
                (sum, [key, val]) =>
                  ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].includes(key)
                    ? sum + (val !== '' ? Number(val) : 0)
                    : sum,
                0
              ),
            }
          : project
      )
    );
  };
  

  
  const submitTimesheet = async () => {
    try {
      const response = await AxiosInstance.post('employee/api/timesheet', {
        employeeId,
        projects: projects.flatMap((project) =>
          weekDates.map((date) => ({
            project_id: project.project_id,
            work_date: date,
            work_hours: project[moment(date).format('dddd').toLowerCase()] || 0,
          }))
        ),
      });

      if (response.status === 200) {
        toastRef.current.showToast('Timesheet submitted successfully!',"success");
        setIsSubmitted(true);
      }
    } catch (error) {
      toastRef.current.showToast('Error submitting timesheet:', error,"danger");
    }
  };

  const handleSubmit = () => {
    const today = moment();
    const endOfWeek = moment().endOf('week');
  
    const hasFutureDates = weekDates.some((date) => {
      const momentDate = moment(date);
      return momentDate.isAfter(today, 'day');
    });
  
    if (hasFutureDates) {
      toastRef.current.showToast('You can only submit the timesheet after the entire week is completed.',"warning");
      return;
    }
  
    submitTimesheet();
  };

  const saveTimesheet = async () => {
    try {
      const response = await AxiosInstance.post("employee/api/save", {
        employeeId,
        projects: projects.flatMap((project) =>
          weekDates.map((date) => ({
            project_id: project.project_id,
            work_date: date,
            work_hours: project[moment(date).format("dddd").toLowerCase()] || 0,
          }))
        ),
      });
  
      if (response.status === 200) {
        toastRef.current.showToast("Timesheet saved successfully!","success");
      }
    } catch (error) {
      toastRef.current.showToast("Failed to save timesheet. Please try again.","danger");
    }
  };
  
  const handleSave = () => {
    saveTimesheet();
  };

  const handleUpdate = async () => {
    try {
      const response = await AxiosInstance.put('/employee/work_hours/update', {
        employeeId,
        projects: projects.flatMap((project) =>
          weekDates.map((date) => ({
            project_id: project.project_id,
            work_date: date,
            work_hours: project[moment(date).format('dddd').toLowerCase()] || 0,
          }))
        ),
      });
  
      if (response.status === 200) {
        toastRef.current.showToast('Timesheet updated successfully!',"success");
        setIsUpdated(true); 

      }
    } catch (error) {
      toastRef.current.showToast('Failed to update timesheet. Please try again.',"danger");
    }
  };
  
  return (
    <ELayout>
    <div className="container mt-4 bg-light p-3 p-md-4 rounded shadow">
      <div className="col-12">
        <Button className="btn btn-secondary mb-3" onClick={handleBack}>
          <ArrowLeftCircle /> Back
        </Button>
      </div>
      <h4 className="mb-3 text-primary fs-6 fs-md-4">Mark Weekly Work Hour</h4>
  
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="table-responsive d-block d-md-none" style={{ maxHeight: "300px", overflowX: "auto" }}>
               <table className="table table-bordered table-striped custom-table text-center align-middle">
               <thead className="table-primary">
            <tr>
              <th>Project ID</th>
              <th>Project Name</th>
              {weekDates.map((date) => (
                <th key={date}>
                  {moment(date).format("dddd")}
                  <br />({moment(date).format("MM-DD-YYYY")})
                </th>
              ))}
              <th>Weekly Hours</th>
            </tr>
          </thead>
          <tbody>
            {projects.length === 0 ? (
              <tr>
                <td colSpan={weekDates.length + 4} className="text-center">
                  No projects assigned
                </td>
              </tr>
            ) : (
              <>
                {projects.map((project) => {
                  const totalWeeklyHours = weekDates.reduce((sum, date) => {
                    const day = moment(date).format("dddd").toLowerCase();
                    const hours = project[day] || 0; 
                    return sum + parseFloat(hours);
                  }, 0);

                  return (
                    <tr key={project.project_id}>
                      <td>{project.project_id}</td>
                      <td>{project.project_name}</td>
                      {weekDates.map((date) => {
                        const isFutureDate = moment(date).isAfter(moment().startOf("day"));
                        const day = moment(date).format("dddd").toLowerCase();
                        const isHoliday = !!holidays[date]; 
                        return (
                          <td key={date}>
                            {isFutureDate || mode === "readonly" ? (
                              <input
                                type="number"
                                className="form-control"
                                value={project[day] !== undefined ? project[day] : ""}
                                disabled
                                min="0"
                                max="8"
                              />
                            ) : (
                              <>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={project[day] !== undefined ? project[day] : ""}
                                  onChange={(e) =>
                                    handleInputChange(project.project_id, day, e.target.value)
                                  }
                                  min="0"
                                  max="8"
                                  disabled={isHoliday || leaveDates.includes(date) || mode === "readonly"}
                                />
                                {isHoliday && (
                                  <span className="text-danger small">
                                    {" ("}
                                    {holidays[date]}
                                    {")"}
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                        );
                      })}
                      <td>
                        {mode === "readonly" ? (
                          <span>{totalWeeklyHours}</span> 
                        ) : (
                          totalWeeklyHours
                        )}
                      </td>
                    </tr>
                  );
                })}

                  {(leaveDates.length > 0 || holidayDates.length > 0) && (
                  <tr className="table-warning">
                      <td colSpan="2" className="text-center">
                          Leave / Holiday
                      </td>
                      {weekDates.map((date) => (
                          <td key={date} className="text-center">
                              {(leaveDates.includes(date) || holidayDates.includes(date)) ? leaveWorkHours : ""}
                          </td>
                      ))}
                      <td className="text-center">-</td>
                  </tr>
              )}
                  {/* Grand Total */}
                  <tr style={{ backgroundColor: "#f0f0f0" }}>
                    <td colSpan={weekDates.length + 2} className="text-end">
                      <strong>Grand Total:</strong>
                    </td>
                    <td>
                      <strong>
                        {weekDates.reduce((grandTotal, date) => {
                          const isLeaveOrHoliday = leaveDates.includes(date) || holidayDates.includes(date);
                          const leaveOrHolidayHours = isLeaveOrHoliday ? leaveWorkHours : 0;

                          const totalProjectHours = projects.reduce((sum, proj) => {
                            const day = moment(date).format("dddd").toLowerCase();
                            const hours = proj[day] !== undefined ? parseFloat(proj[day]) || 0 : 0;
                            return sum + hours;
                          }, 0);

                          return grandTotal + totalProjectHours + leaveOrHolidayHours;
                        }, 0)}
                      </strong>
                    </td>
                  </tr>

                    </>
                  )}
                  
                </tbody>
                
              </table>
            </div>

            <div className="table-responsive d-none d-md-block">
               <table className="table table-bordered table-striped custom-table text-center align-middle">
               <thead className="table-primary">
                  <tr>
                    <th>Project ID</th>
                    <th>Project Name</th>
                    {weekDates.map((date) => (
                      <th key={date}>
                        {moment(date).format("dddd")}
                        <br />({moment(date).format("MM-DD-YYYY")})
                      </th>
                    ))}
                    <th>Weekly Hours</th>
                  </tr>
                </thead>
          <tbody>
            {projects.length === 0 ? (
              <tr>
                <td colSpan={weekDates.length + 4} className="text-center">
                  No projects assigned
                </td>
              </tr>
            ) : (
              <>
                {projects.map((project) => {
                  const totalWeeklyHours = weekDates.reduce((sum, date) => {
                    const day = moment(date).format("dddd").toLowerCase();
                    const hours = project[day] || 0; 
                    return sum + parseFloat(hours);
                  }, 0);

                  return (
                    <tr key={project.project_id}>
                      <td>{project.project_id}</td>
                      <td>{project.project_name}</td>
                      {weekDates.map((date) => {
                        const isFutureDate = moment(date).isAfter(moment().startOf("day"));
                        const day = moment(date).format("dddd").toLowerCase();
                        const isHoliday = !!holidays[date]; 
                        return (
                          <td key={date}>
                            {isFutureDate || mode === "readonly" ? (
                              <input
                                type="number"
                                className="form-control"
                                value={project[day] !== undefined ? project[day] : ""}
                                disabled
                                min="0"
                                max="8"
                              />
                            ) : (
                              <>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={project[day] !== undefined ? project[day] : ""}
                                  onChange={(e) =>
                                    handleInputChange(project.project_id, day, e.target.value)
                                  }
                                  min="0"
                                  max="8"
                                  disabled={isHoliday || leaveDates.includes(date) || mode === "readonly"}
                                />
                                {isHoliday && (
                                  <span className="text-danger small">
                                    {" ("}
                                    {holidays[date]}
                                    {")"}
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                        );
                      })}
                      <td>
                        {mode === "readonly" ? (
                          <span>{totalWeeklyHours}</span> 
                        ) : (
                          totalWeeklyHours
                        )}
                      </td>
                    </tr>
                  );
                })}

                  {(leaveDates.length > 0 || holidayDates.length > 0) && (
                  <tr className="table-warning">
                      <td colSpan="2" className="text-center">
                          Leave / Holiday
                      </td>
                      {weekDates.map((date) => (
                          <td key={date} className="text-center">
                              {(leaveDates.includes(date) || holidayDates.includes(date)) ? leaveWorkHours : ""}
                          </td>
                      ))}
                      <td className="text-center">-</td>
                  </tr>
              )}
                  {/* Grand Total */}
                  <tr style={{ backgroundColor: "#f0f0f0" }}>
                    <td colSpan={weekDates.length + 2} className="text-end">
                      <strong>Grand Total:</strong>
                    </td>
                    <td>
                      <strong>
                        {weekDates.reduce((grandTotal, date) => {
                          const isLeaveOrHoliday = leaveDates.includes(date) || holidayDates.includes(date);
                          const leaveOrHolidayHours = isLeaveOrHoliday ? leaveWorkHours : 0;

                          const totalProjectHours = projects.reduce((sum, proj) => {
                            const day = moment(date).format("dddd").toLowerCase();
                            const hours = proj[day] !== undefined ? parseFloat(proj[day]) || 0 : 0;
                            return sum + hours;
                          }, 0);

                          return grandTotal + totalProjectHours + leaveOrHolidayHours;
                        }, 0)}
                      </strong>
                    </td>
                  </tr>

                    </>
                  )}
                  
                </tbody>
                
              </table>
            </div>
          </div>
        </div>
      </div>
  
      {/* Display Buttons Based on Mode */}
      <div className="d-flex flex-column flex-md-row justify-content-center mt-3 gap-2">
        {mode === "new" && !isSubmitted && (
          <>
            <button onClick={handleSave} className="btn btn-warning btn-sm w-auto">
              Save 
            </button>
            <button onClick={handleSubmit} className="btn btn-success btn-sm w-auto">
              Submit
            </button>
          </>
        )}
  
        {mode === "edit" && !isSubmitted && (
          <>
            <button  onClick={handleSave} className="btn btn-warning btn-sm w-auto">
              Save 
            </button>
            <button onClick={handleSubmit} className="btn btn-success btn-sm w-auto">
              Submit
            </button>
          </>
        )}
  
        {mode === "rejected" && !isUpdated && (
          <>
            <button  onClick={handleUpdate} className="btn btn-primary btn-sm w-auto">
              Update
            </button>
          </>
        )}
      </div>
    </div>
    <ToastNotification ref={toastRef}/>
  </ELayout>
  );
};
export default TimesheetTable;
